/*
  Post-load Enrichment Engine placement.

  After the initial page load (SSR or publisher /results), the client POSTs
  ee_token + result_context to rampjs-server /enrich. The server calls EE and
  returns SERP-rendered HTML fragments; we inject them into
  `[data-enrichment-slot]` placeholders left by SSR.
*/

import { CONSOLE_CAPTURE_PREFIX } from "@ascent/debugger";
import type { Response as PapiResponse } from "@ascent/papi-core";
import type { EnrichBody, EnrichResponse } from "@rampjs/server";
// Subpath only — the main `@serp/renderer` barrel pulls the full SSR renderer
// into ramp.js. extractEeToken / result_context.results (WEB_TYPES) live here
// so post-load and SSR stay on the same wire contract.
import {
  buildResultContext as buildSharedResultContext,
  extractEeToken,
} from "@serp/renderer/enrichment/context";

export { extractEeToken };

function isDebug(): boolean {
  // Prefer the compile-time `__DEBUG__` define (ramp.js bundles); tests stub it
  // on globalThis via vitest setup.
  try {
    return Boolean(__DEBUG__);
  } catch {
    return Boolean(
      (globalThis as unknown as { __DEBUG__?: boolean }).__DEBUG__,
    );
  }
}

/** Escape a value for use inside a CSS attribute selector. */
function cssEscapeAttr(value: string): string {
  if (typeof CSS !== "undefined" && typeof CSS.escape === "function") {
    return CSS.escape(value);
  }
  return value.replace(/\\/g, "\\\\").replace(/"/g, '\\"');
}

/**
 * Derive POST /enrich URL from the baked-in RAMPJS_SERVER_URL (/results).
 * Must use the compile-time `RAMPJS_SERVER_URL` define (same as server.ts) —
 * reading `globalThis.RAMPJS_SERVER_URL` is always empty in the browser bundle,
 * which made enrich POSTs hit the SERP origin instead of rampjs-server.
 */
export function enrichEndpointUrl(serverUrl?: string): string {
  return swapResultsPath(serverUrl, "enrich");
}

/**
 * Derive POST /interact URL from the baked-in RAMPJS_SERVER_URL (/results).
 */
export function interactEndpointUrl(serverUrl?: string): string {
  return swapResultsPath(serverUrl, "interact");
}

function swapResultsPath(
  serverUrl: string | undefined,
  segment: string,
): string {
  const base = serverUrl ?? RAMPJS_SERVER_URL;
  try {
    const url = new URL(base, window.location.href);
    if (url.pathname.endsWith("/results")) {
      url.pathname = url.pathname.replace(/\/results\/?$/, `/${segment}`);
    } else if (url.pathname.endsWith("/")) {
      url.pathname = `${url.pathname}${segment}`;
    } else {
      url.pathname = `${url.pathname}/${segment}`;
    }
    return url.toString();
  } catch {
    return base.replace(/\/results\/?$/, `/${segment}`);
  }
}

/**
 * Proxy EE POST /v1/interaction through rampjs-server. Widget scripts call
 * `window.__rampjsInteract(token, params)`.
 */
export async function rampjsInteract(
  token: string,
  params?: Record<string, unknown>,
): Promise<unknown> {
  const res = await fetch(interactEndpointUrl(), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      token,
      params: params ?? {},
      pageUrl: window.location.href,
    }),
  });
  const json = (await res.json().catch(() => ({}))) as {
    payload?: unknown;
    error?: { message?: string };
  };
  if (!res.ok) {
    throw Object.assign(
      new Error(
        json.error?.message || `interaction failed with status ${res.status}`,
      ),
      { status: res.status, body: json },
    );
  }
  if (json.error?.message && json.payload === undefined) {
    throw Object.assign(new Error(json.error.message), {
      status: res.status,
      body: json,
    });
  }
  return json.payload !== undefined ? json.payload : json;
}

function installInteractHelper(): void {
  window.__rampjsInteract = rampjsInteract;
}

/**
 * Timezone module: prefer the browser's default locale hour cycle (OS / language
 * settings). Falls back to an explicit 2-letter country when Intl is unavailable.
 * `world` = 24h (14:30), `us` = 12h (2:30 PM).
 */
export function resolveDateAndTimeFormat(
  explicitCountryCode?: string,
): "world" | "us" {
  try {
    const opts = new Intl.DateTimeFormat(undefined, {
      hour: "numeric",
    }).resolvedOptions() as Intl.ResolvedDateTimeFormatOptions & {
      hourCycle?: "h11" | "h12" | "h23" | "h24";
    };
    if (opts.hourCycle === "h12" || opts.hourCycle === "h11") return "us";
    if (opts.hourCycle === "h23" || opts.hourCycle === "h24") return "world";
    if (opts.hour12 === true) return "us";
    if (opts.hour12 === false) return "world";
  } catch {
    /* ignore */
  }
  return (explicitCountryCode || "").trim().toUpperCase() === "US"
    ? "us"
    : "world";
}

export function buildResultContext(
  response: PapiResponse | undefined,
): EnrichBody["result_context"] | undefined {
  const ctx = buildSharedResultContext(response, window.location.href);
  if (!ctx) return undefined;
  return {
    ...ctx,
    // Post-load: browser hour-cycle wins over the SSR country fallback.
    date_and_time_format: resolveDateAndTimeFormat(
      ctx.date_and_time_format === "us" ? "US" : undefined,
    ),
  };
}

function shouldSkipInlineScript(
  content: string,
  module: string | undefined,
): boolean {
  if (module) {
    const existing = document.querySelector(
      `script[data-enrichment-module="${cssEscapeAttr(module)}"]`,
    );
    // Missing marker means "not this module" — never fall through to a
    // content match against a sibling card's shared observer prefix.
    if (!existing) return false;
    // A prior inject may have left a broken script tag (exec error) without
    // registering init hooks — allow a retry in that case.
    if (
      module === "unitconverter" &&
      typeof window.__initUnitConverters !== "function"
    ) {
      existing.remove();
      return false;
    }
    if (
      module === "aviationstack" &&
      typeof window.__initAviationStacks !== "function"
    ) {
      existing.remove();
      return false;
    }
    if (
      module === "openweathermap" &&
      typeof window.__initOpenWeatherMaps !== "function"
    ) {
      existing.remove();
      return false;
    }
    if (module === "finnhub" && typeof window.__initFinnhubs !== "function") {
      existing.remove();
      return false;
    }
    if (
      module === "calculator" &&
      typeof window.__initCalculators !== "function"
    ) {
      existing.remove();
      return false;
    }
    if (
      module === "dictionary" &&
      typeof window.__initDictionaries !== "function"
    ) {
      existing.remove();
      return false;
    }
    return true;
  }

  // Same full script only (wikipedia / timezone / iplookup share one
  // impression-observer body). A 64-character prefix is not unique:
  // dictionary, aviationstack, Finnhub, and OpenWeatherMap all start with
  // that observer stub, so a prefix match would skip the later module and
  // never register its __init* hook.
  return Array.from(document.scripts).some(
    (s) => (s.textContent || "") === content,
  );
}

function applyHeadScripts(
  head: EnrichResponse["enrichments"][number]["head"] | undefined,
  module?: string,
): void {
  if (!head?.scripts?.length) return;
  for (const script of head.scripts) {
    if (script.content) {
      if (shouldSkipInlineScript(script.content, module)) {
        continue;
      }
      const el = document.createElement("script");
      el.textContent = script.content;
      if (module) {
        el.setAttribute("data-enrichment-module", module);
      }
      if (script.attributes) {
        for (const [k, v] of Object.entries(script.attributes)) {
          if (v === false) continue;
          el.setAttribute(k, v === true ? "" : String(v));
        }
      }
      try {
        document.head.appendChild(el);
      } catch (error) {
        if (isDebug()) {
          console.warn(
            `${CONSOLE_CAPTURE_PREFIX} Enrichment Engine`,
            `failed to execute ${module ?? "inline"} enrichment script`,
            error,
          );
        }
      }
    } else if (script.src) {
      if (
        document.querySelector(`script[src="${cssEscapeAttr(script.src)}"]`)
      ) {
        continue;
      }
      const el = document.createElement("script");
      el.src = script.src;
      if (module) {
        el.setAttribute("data-enrichment-module", module);
      }
      document.head.appendChild(el);
    }
  }
}

function runUnitConverterInit(module: string | undefined): void {
  if (module !== "unitconverter") return;
  if (typeof window.__initUnitConverters === "function") {
    window.__initUnitConverters();
    return;
  }
  if (isDebug()) {
    console.warn(
      `${CONSOLE_CAPTURE_PREFIX} Enrichment Engine`,
      "unitconverter HTML placed but __initUnitConverters is missing — " +
        "head.scripts likely did not execute (CSP, dedup, or script error)",
    );
  }
}

/**
 * The impression observer in each module script is gated on a page-global flag,
 * so its one scan only sees the cards already in the DOM. Re-scan after every
 * card so later ones still fire their beacons. Safe to call repeatedly — the
 * scan skips nodes already stamped `data-impression-fired`.
 */
function runBeaconRescan(): void {
  const scan =
    window.__RAMPJS_EE_IMPRESSION__?.scan ?? window.__rampjsEeScanBeacons;
  if (typeof scan !== "function") return;
  try {
    scan();
  } catch (error) {
    if (isDebug()) {
      console.warn(
        `${CONSOLE_CAPTURE_PREFIX} Enrichment Engine`,
        "impression beacon re-scan failed",
        error,
      );
    }
  }
}

function runCalculatorInit(module: string | undefined): void {
  // Calculator module: the bundle only scans for un-hydrated nodes when it
  // executes, and the script is deduped by module, so every card after the
  // first needs this explicit re-init.
  if (module !== "calculator") return;
  if (typeof window.__initCalculators === "function") {
    window.__initCalculators();
    return;
  }
  if (isDebug()) {
    console.warn(
      `${CONSOLE_CAPTURE_PREFIX} Enrichment Engine`,
      "calculator module: HTML placed but __initCalculators is missing — " +
        "head.scripts likely did not execute (CSP, dedup, or script error)",
    );
  }
}

function runAviationstackInit(module: string | undefined): void {
  // Aviationstack module: re-hydrate after post-load inject (DOMContentLoaded already fired).
  if (module !== "aviationstack") return;
  if (typeof window.__initAviationStacks === "function") {
    window.__initAviationStacks();
    return;
  }
  if (isDebug()) {
    console.warn(
      `${CONSOLE_CAPTURE_PREFIX} Enrichment Engine`,
      "aviationstack module: HTML placed but __initAviationStacks is missing — " +
        "head.scripts likely did not execute (CSP, dedup, or script error)",
    );
  }
}

function runOpenWeatherMapInit(module: string | undefined): void {
  // OpenWeatherMap module: re-hydrate after post-load inject (Chart.js + tabs).
  if (module !== "openweathermap") return;
  if (typeof window.__initOpenWeatherMaps === "function") {
    window.__initOpenWeatherMaps();
    return;
  }
  if (isDebug()) {
    console.warn(
      `${CONSOLE_CAPTURE_PREFIX} Enrichment Engine`,
      "openweathermap module: HTML placed but __initOpenWeatherMaps is missing — " +
        "head.scripts likely did not execute (CSP, dedup, or script error)",
    );
  }
}

function runFinnhubInit(module: string | undefined): void {
  // Finnhub ticker + profile: re-hydrate after post-load inject so sibling
  // widgets pick up a shared symbol dropdown.
  if (module !== "finnhub" && module !== "finnhub_profile") return;
  if (typeof window.__initFinnhubs === "function") {
    window.__initFinnhubs();
    return;
  }
  if (isDebug()) {
    console.warn(
      `${CONSOLE_CAPTURE_PREFIX} Enrichment Engine`,
      "finnhub module: HTML placed but __initFinnhubs is missing — " +
        "head.scripts likely did not execute (CSP, dedup, or script error)",
    );
  }
}

function runDictionaryInit(module: string | undefined): void {
  // Dictionary module: the script is deduped by module, so every card after
  // the first needs this explicit re-init for synonym toggles / see-all.
  if (module !== "dictionary") return;
  if (typeof window.__initDictionaries === "function") {
    window.__initDictionaries();
    return;
  }
  if (isDebug()) {
    console.warn(
      `${CONSOLE_CAPTURE_PREFIX} Enrichment Engine`,
      "dictionary module: HTML placed but __initDictionaries is missing — " +
        "head.scripts likely did not execute (CSP, dedup, or script error)",
    );
  }
}

/**
 * Inject enrichment HTML into `[data-enrichment-slot]` placeholders.
 */
export function injectEnrichments(
  enrichments: EnrichResponse["enrichments"],
): number {
  installInteractHelper();
  let placed = 0;
  // A slot can legitimately hold several cards (config_token maps one slot name
  // to a list of modules, and the SSR path in the renderer's
  // injectEnrichmentCards emits them as siblings). Reset a placeholder the
  // first time this call fills it, then append, so a second card for the same
  // slot does not replace the first.
  const filledSlots = new Set<string>();
  for (const item of enrichments) {
    if (!item.slot || !item.html?.trim()) continue;
    const el = document.querySelector(
      `[data-enrichment-slot="${cssEscapeAttr(item.slot)}"]`,
    );
    if (!(el instanceof HTMLElement)) continue;
    if (!filledSlots.has(item.slot)) {
      el.innerHTML = "";
      filledSlots.add(item.slot);
    }
    el.insertAdjacentHTML("beforeend", item.html);
    el.hidden = false;
    el.removeAttribute("hidden");
    stampInteractUrl(el, item.module);
    applyHeadScripts(item.head, item.module);
    runUnitConverterInit(item.module);
    runCalculatorInit(item.module);
    runAviationstackInit(item.module);
    runOpenWeatherMapInit(item.module);
    runFinnhubInit(item.module);
    runDictionaryInit(item.module);
    runBeaconRescan();
    placed += 1;
  }
  return placed;
}

function stampInteractUrl(root: HTMLElement, module: string | undefined): void {
  if (module !== "finnhub") return;
  // Stamp every card in the slot, not just the first — a slot can hold more
  // than one. Re-stamping an already-stamped card writes the same URL.
  const cards = root.matches(".finnhub-qi-top")
    ? [root]
    : root.querySelectorAll(".finnhub-qi-top");
  const url = interactEndpointUrl();
  for (const card of cards) {
    if (card instanceof HTMLElement) {
      card.setAttribute("data-interact-url", url);
    }
  }
}

export type RunEnrichmentResult = {
  skipped?: boolean;
  reason?: string;
  enrichments?: EnrichResponse["enrichments"];
  requestId?: string | null;
  error?: string;
  placed?: number;
  debug?: EnrichResponse["debug"];
};

/**
 * If the PAPI response has an ee_token, POST /enrich and inject returned HTML.
 * Never throws — enrichment is best-effort after page load.
 *
 * `slotsReady` resolves once the `[data-enrichment-slot]` placeholders exist.
 * Gated pages place them after RSoC, so the POST starts now and injection
 * waits for that placement.
 */
export async function runEnrichment(
  response: PapiResponse | undefined,
  slotsReady?: Promise<unknown>,
): Promise<RunEnrichmentResult> {
  installInteractHelper();
  const eeToken = extractEeToken(response);
  if (!eeToken) {
    return { skipped: true, reason: "no ee_token" };
  }

  const resultContext = buildResultContext(response);
  if (!resultContext) {
    return { skipped: true, reason: "no result_context query" };
  }

  const body: EnrichBody = {
    pageUrl: window.location.href,
    ee_token: eeToken,
    result_context: resultContext,
    external_request_id:
      typeof response?.request_id === "string"
        ? response.request_id
        : undefined,
  };

  const url = enrichEndpointUrl();
  try {
    const res = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    const json = (await res.json().catch(() => ({}))) as EnrichResponse;
    if (!res.ok && !json.enrichments?.length) {
      const message =
        json.error?.message || `enrich failed with status ${res.status}`;
      if (isDebug()) {
        console.warn(`${CONSOLE_CAPTURE_PREFIX} Enrichment Engine`, message);
      }
      return {
        error: message,
        enrichments: json.enrichments ?? [],
        requestId: json.requestId,
        debug: json.debug,
        placed: 0,
      };
    }

    await slotsReady;
    const placed = injectEnrichments(json.enrichments ?? []);
    if (isDebug()) {
      console.log(`${CONSOLE_CAPTURE_PREFIX} Enrichment Engine`, {
        requestId: json.requestId,
        placed,
        slots: (json.enrichments ?? []).map((e: { slot: string }) => e.slot),
        debug: json.debug,
      });
    }
    return {
      enrichments: json.enrichments ?? [],
      requestId: json.requestId,
      debug: json.debug,
      placed,
      error: json.error?.message,
    };
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    if (isDebug()) {
      console.warn(`${CONSOLE_CAPTURE_PREFIX} Enrichment Engine`, message);
    }
    return { error: message, placed: 0 };
  }
}
